import { setLoadableErrorAttributes } from '@confluence/monitoring/entry-points/error-attributes.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';

declare global {
	interface Window {
		__LOADABLE__?: string[];
		__EMPTY_LOADABLE__?: string[];
	}
}

export const LOADING_TIMEOUT: number = process.env.NODE_ENV === 'production' ? 8500 : 30000;

// The prefix of the ids that LoadableReactLazyBabelPlugin gives React.lazy calls
export const REACT_LAZY_ID_PREFIX = 'rl-';

// The part of a loadable id before the first `:`, which __EMPTY_LOADABLE__ lists
export const getComponentName = (nameOrId: string): string => {
	const separatorIndex = nameOrId.indexOf(':');
	return separatorIndex === -1 ? nameOrId : nameOrId.slice(0, separatorIndex);
};

let reportedIds: Set<string> | undefined;

/**
 * The ids in __LOADABLE__ that did not render empty. SSR sets the loadable globals before any client
 * module runs, so they are read once.
 */
export function getReportedIds(): Set<string> {
	if (!reportedIds) {
		const emptyComponentNames = new Set(window.__EMPTY_LOADABLE__ || []);
		reportedIds = new Set(
			(window.__LOADABLE__ || []).filter(
				(nameOrId) => !emptyComponentNames.has(getComponentName(nameOrId)),
			),
		);
	}
	return reportedIds;
}

/**
 * Whether the server reported this React.lazy id. preloadIfNeeded only acts on reported ids, so the
 * client registers just those and skips the loadable scan for every other React.lazy definition.
 */
export function isReportedReactLazyId(id: string): boolean {
	return id.startsWith(REACT_LAZY_ID_PREFIX) && getReportedIds().has(id);
}

export function reportLoadableTimeouts(): void {
	const loadableIds = window.__LOADABLE_TIMEOUTS__;
	if (!loadableIds?.length) return;

	const error = new Error(`Loadable initialization timeout`);
	setLoadableErrorAttributes(error, { loadableIds });
	// Timeouts are common in normal operation, so they go to a separate attribution to track them
	// without flooding monitoring.
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	if (navigator.onLine) {
		getMonitoringClient().submitError(error, { attribution: 'backbone-loadable' });
	}
}

export function _resetLoadableGlobalsOnlyForTest(): void {
	reportedIds = undefined;
}
